import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter
} from '@reduxjs/toolkit'
import { client } from '../../../../api/client'
import { addAlert } from '../alerts/alertSlice';
import { setNodesActionCreator } from '../node/nodeSlice';
import { setEdgesActionCreator } from '../edge/edgeSlice';
import { BASE_URL } from '../../../../config';
import { sanitizeTemplateSettings } from '../view/viewSlice';
import { PAGE_SIZE_A4, PAGE_SIZE_A3, PAGE_SIZE_ARCH_D } from '../../../../utilities/constants/pageSizeConstants';

const VALID_PAGE_SIZES = new Set([PAGE_SIZE_A4, PAGE_SIZE_A3, PAGE_SIZE_ARCH_D]);

const sanitizeNodesForSave = (nodes = []) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node) => {
    if (!node || typeof node !== 'object') return node;
    const clone = JSON.parse(JSON.stringify(node));
    delete clone.selected;
    delete clone.dragging;
    delete clone.positionAbsolute;
    delete clone.style;
    return clone;
  });
};

const sanitizeEdgesForSave = (edges = []) => {
  if (!Array.isArray(edges)) return [];
  return edges.map((edge) => {
    if (!edge || typeof edge !== 'object') return edge;
    const clone = JSON.parse(JSON.stringify(edge));
    delete clone.selected;
    delete clone.dragging;
    return clone;
  });
};

const diagramAdapter = createEntityAdapter()

const initialState = diagramAdapter.getInitialState({
  diagramLoading: false,
  diagramError: null,
  selectedId: null,
  modal: false,
  instance: null,
  diagramHistory: []
});  

// Thunk 
export const fetchDiagrams = createAsyncThunk('diagrams/fetchDiagrams', async () => {
  const response = await client.get(`${BASE_URL}/diagram`);
  return response;
});

export const fetchDiagram = createAsyncThunk(
  'diagram/fetchDiagram',
  async (form, { dispatch }) => {
    try {
      const { formValues } = form;
      const response = await client.get(
        `${BASE_URL}/diagram/${formValues.id}`,
        { payload: formValues }
      );
  
      return response;
    } catch (err) {
      console.error('Error fetching diagram:', err);
      throw err;
    }
  }
);



export const updateDiagram = createAsyncThunk(
'diagram/updateDiagram',
async (_, { getState }) => {
  try {
    const state = getState();
    const { present } = state.history;
    const { selectedId } = state.diagram;
    const {
      pageSize,
      scaleCm,
      labelFontPx,
      symbolLabelOverrides,
      gridGapPx,
      autosaveIntervalMinutes,
      hideParameterLabelsDefault,
      attributeLabelSeparator,
      templateSettings: currentTemplateSettings
    } = state.view || {};

    if (!selectedId) {
      throw new Error('No diagram is selected');
    }

    // Merge view settings so icon/text sizes persist with the diagram
    const templateSettings = sanitizeTemplateSettings(currentTemplateSettings);
    const baseData = present ? JSON.parse(JSON.stringify(present)) : {};
    baseData.nodes = sanitizeNodesForSave(state.node?.nodes);
    baseData.edges = sanitizeEdgesForSave(state.edge?.edges);
    baseData.templateSettings = templateSettings;
    const existingView = baseData.view && typeof baseData.view === 'object' ? baseData.view : {};
    const viewPayload = { ...existingView };
    if (typeof scaleCm === 'number' && scaleCm > 0) {
      viewPayload.scaleCm = scaleCm;
    }
    if (typeof labelFontPx === 'number' && labelFontPx > 0) {
      viewPayload.labelFontPx = labelFontPx;
    }
    if (symbolLabelOverrides && typeof symbolLabelOverrides === 'object' && !Array.isArray(symbolLabelOverrides)) {
      viewPayload.symbolLabelOverrides = symbolLabelOverrides;
    }
    if (typeof gridGapPx === 'number' && gridGapPx >= 25 && gridGapPx <= 120) {
      viewPayload.gridGapPx = gridGapPx;
    }
    if (typeof autosaveIntervalMinutes === 'number' && autosaveIntervalMinutes > 0) {
      viewPayload.autosaveIntervalMinutes = autosaveIntervalMinutes;
    }
    if (typeof hideParameterLabelsDefault === 'boolean') {
      viewPayload.hideParameterLabelsDefault = hideParameterLabelsDefault;
    }
    if (typeof attributeLabelSeparator === 'string') {
      viewPayload.attributeLabelSeparator = attributeLabelSeparator;
    }
    if (typeof pageSize === 'string' && VALID_PAGE_SIZES.has(pageSize)) {
      viewPayload.pageSize = pageSize;
    }
    baseData.view = viewPayload;
    const updatePayload = {
      id: selectedId,
      data: JSON.stringify(baseData),
    };

    const response = await client.put(
      `${BASE_URL}/diagram/${selectedId}/update`,
      updatePayload, // Ensures the payload is JSON-encoded
      {
        headers: {
          'Content-Type': 'application/json', // Sets the correct Content-Type header
        },
      }
    );

    return response.data;
  } catch (err) {
    console.error('Error in updateDiagram:', err);
    throw err;
  }
}
);



export const createDiagram = createAsyncThunk('diagram/createDiagram', async (payload, { getState, dispatch }) => {
  try {
    const response = await client.post(`${BASE_URL}/diagram/store`, payload)

    dispatch(addAlert({
      duration: 2000,
      severity: 'success',
      message: 'Diagram stored successfully'
    }));         

    return { diagram: response.data?.diagram };
  } catch (err) {

    dispatch(addAlert({
      duration: 2000,
      severity: 'error',
      message: 'Error while storing the diagram'
    }));     

    throw err;
  }
});


export const deleteDiagram = createAsyncThunk(
  'diagram/deleteDiagram',
  async (diagramId, { rejectWithValue }) => {
    try {
      if (!diagramId) {
        throw new Error('No diagram ID provided');
      }

      const response = await client.delete(
        `${BASE_URL}/diagram/${diagramId}/delete`,
        {
          data: {
            id: diagramId,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return { diagram: response.data?.diagram };
    } catch (err) {
      console.error('Error in deleteDiagram:', err);
      // `rejectWithValue` allows you to return a custom error payload to the reducer
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateDiagramInfo = createAsyncThunk(
  'diagram/updateInfo',
  async (payload, { rejectWithValue }) => {
    const { id, label, description } = payload;
    try {
      const response = await client.put(
        `${BASE_URL}/diagram/${id}/update-info`,
        { label, description },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return { diagram: response.data?.diagram || { id, label, description } };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const moveDiagramToFolder = createAsyncThunk(
  'diagram/moveToFolder',
  async (payload, { rejectWithValue }) => {
    const { diagramId, folderId } = payload;
    try {
      const response = await client.put(
        `${BASE_URL}/diagram/${diagramId}/move`,
        { folder: folderId },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      return {
        diagram:
          response.data?.diagram || { id: diagramId, folder: folderId },
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Slice / Reducer
const diagramSlice = createSlice({
  name: 'diagram',
  initialState,
  reducers: {
    setSelectedDiagram(state, action) {
      state.selectedId = action.payload;
    },      
    openDiagramModal(state, action) {
      const diagramId = action.payload.id;
      const diagramModal = action.payload.modal;
      state.modal = diagramModal;
      state.selectedId = diagramId;
    },
    closeDiagramModal(state, action) {
      state.modal = null;
      state.selectedId = null;
    },
    diagramToggled(state, action) {
      const diagramId = action.payload;
      const diagram = state.entities[diagramId];
      diagram.hide = !diagram.hide;
    },
    diagramColorSelected: {
      reducer(state, action) {
        const { color, diagramId } = action.payload;
        state.entities[diagramId].color = color;
      },
      prepare(diagramId, color) {
        return {
          payload: { diagramId, color },
        }
      }
    },
    diagramDeleted: diagramAdapter.removeOne,
    allDiagramCompleted(state, action) {
      Object.values(state.entities).forEach(diagram => {
        diagram.completed = true;
      })
    },
    completedDiagramsCleared(state, action) {
      const completedIds = Object.values(state.entities)
        .filter(diagram => diagram.completed)
        .map(diagram => diagram.id)
        diagramAdapter.removeMany(state, completedIds);
    },
    updateInstance(state, action) {
      const { instance } = action.payload;
      state.instance = instance;
    },
    pushDiagramHistory(state, action) {
      state.diagramHistory.push(action.payload);
    },
    popDiagramHistory(state, action) {
      const previousDiagram = state.diagramHistory.pop();
      if (previousDiagram) {
        state.selectedId = previousDiagram.selectedId;
        state.currentDiagram = previousDiagram.diagramData;
      }
    },
    clearDiagramHistory(state) {
      state.diagramHistory = [];
    }
    ,
    unlinkDiagramsByFolderId(state, action) {
      const folderId = action.payload;
      Object.values(state.entities).forEach(diagram => {
        if (diagram.folder === folderId) {
          diagram.folder = null;
        }
      });
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDiagrams.pending, (state, action) => {
          state.diagramLoading = true;
          state.diagramError = null;
      })
      .addCase(fetchDiagrams.fulfilled, (state, action) => {
          diagramAdapter.setAll(state, action.payload);
          state.diagramLoading = false;
      })
      .addCase(fetchDiagrams.rejected, (state, action) => {
          state.diagramLoading = false;
          state.diagramError = action.error.message;
      })
      .addCase(createDiagram.fulfilled, (state, action) => {
        const newDiagram = action.payload.diagram;
        diagramAdapter.upsertOne(state, newDiagram);          
      })
      .addCase(deleteDiagram.fulfilled, (state, action) => {
        const deleted = action.payload.diagram;
        diagramAdapter.removeOne(state, deleted.id);
      })
      .addCase(updateDiagramInfo.pending, (state, action) => {
        const { id, label, description } = action.meta.arg;
        if (state.entities[id]) {
          state.entities[id].label = label;
          state.entities[id].description = description;
        }
      })
      .addCase(updateDiagramInfo.fulfilled, (state, action) => {
        const updated = action.payload.diagram;
        diagramAdapter.upsertOne(state, updated);
      })
      .addCase(moveDiagramToFolder.pending, (state, action) => {
        const { diagramId, folderId } = action.meta.arg;
        if (state.entities[diagramId]) {
          state.entities[diagramId].folder = folderId;
        }
      })
      .addCase(moveDiagramToFolder.fulfilled, (state, action) => {
        const updated = action.payload.diagram;
        diagramAdapter.upsertOne(state, updated);
      })
      .addCase(fetchDiagram.pending, (state) => {
        state.diagramLoading = true;
        state.diagramError = null;
      })
      .addCase(fetchDiagram.fulfilled, (state, action) => {
        diagramAdapter.upsertOne(state, action.payload);
        state.selectedId = action.payload.id;
        state.diagramLoading = false;
      })
      .addCase(fetchDiagram.rejected, (state, action) => {
        state.diagramLoading = false;
        state.diagramError = action.error.message;
      });        
  }
});

export const {
  setSelectedDiagram,
  allDiagramCompleted,
  completedDiagramsCleared,
  todoAdded,
  diagramColorSelected,
  diagramDeleted,
  diagramToggled,
  openDiagramModal,
  closeDiagramModal,
  pushDiagramHistory,
  popDiagramHistory,
  clearDiagramHistory
  ,unlinkDiagramsByFolderId
} = diagramSlice.actions;

export default diagramSlice.reducer;

// Selectors
export const { 
  selectAll: selectDiagrams, 
  selectById: selectDiagramById 
} = diagramAdapter.getSelectors(state => state.diagram);


export const getLatestDiagrams = createSelector(
  selectDiagrams,
  diagrams => diagrams.slice().sort((a, b) => b.created_at.localeCompare(a.created_at))
);

// Selector to retrieve all tags from existing diagrams
export const selectAllDiagramTags = createSelector(
  selectDiagrams,
  diagrams => {
    const tagSet = new Set();
    diagrams.forEach((diagram) => {
      diagram.question_tags.forEach((tagId) => {
        tagSet.add(tagId);
      });
    });
    return Array.from(tagSet);
  }
);

export const selectDiagramsWithTags = createSelector(
selectDiagrams,
(_, examTags) => examTags, // Pass examTags as the second argument to the selector
(diagrams, examTags) => {
  const filteredDiagrams = Object.values(diagrams).filter((diagram) => {
    return diagram.question_tags?.some((item) => examTags?.includes(item));
  });
  return filteredDiagrams;
}
);

/*
export const getDiagramsByTags = createSelector(
  selectDiagrams,
  diagrams => (tags) => {

    if(tags.length == 0) {
      return [];
    }

    let filteredDiagrams = diagrams;  
    filteredDiagrams = filteredDiagrams.filter((diagram) =>
      tags.every((selectedTagId) => diagram.question_tags.includes(selectedTagId))
    );

    return filteredDiagrams;
  }
);  
*/
const getSearchInput = (_, searchInput) => searchInput;

export const getFilteredDiagrams = createSelector(
  [selectDiagrams, getSearchInput, (state) => state.tag.selectedFilteredIds, (_, sortOrder) => sortOrder],
  (diagrams, searchInput, selectedFilteredIds, sortOrder) => {
    let filteredDiagrams = diagrams;

    if (selectedFilteredIds.length > 0) {
      filteredDiagrams = filteredDiagrams.filter((diagram) =>
        selectedFilteredIds.every((selectedTagId) => diagram.question_tags.includes(selectedTagId))
      );
    }

    if (searchInput.trim() !== '') {
      filteredDiagrams = filteredDiagrams.filter((diagram) =>
        diagram.question_label.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    if (sortOrder === 'asc') {
      filteredDiagrams = filteredDiagrams.sort((a, b) => a.created_at.localeCompare(b.created_at));
    } else {
      filteredDiagrams = filteredDiagrams.sort((a, b) => b.created_at.localeCompare(a.created_at));
    }      

    return filteredDiagrams;
  }
);

export const getFilteredDiagramsByText = createSelector(
  [selectDiagrams, getSearchInput],
  (diagrams, searchInput) => {
    // If no search, return everything
    if (!searchInput.trim()) return diagrams;

    // Filter by "label" (adjust to your field if different)
    const lowerSearch = searchInput.toLowerCase();
    return diagrams.filter((diagram) =>
      diagram.label?.toLowerCase().includes(lowerSearch)
    );
  }
);

export const selectDiagramError = (state) => state.diagram.diagramError;


export const selectSelectedDiagram = (state) =>
  selectDiagramById(state, state.diagram.selectedId);
